import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { LwSubscriptionStatus, RecordSource } from 'constants/constants';
import { THEME_MESSAGES } from 'utils/constants';
import { getIsNotFound } from 'utils/getIsNotFound';
import { getIndividualBusiness } from 'api/admin/businesses/businesses';
import { ApiError } from 'types/api';

interface GetIndividualBusinessOutput {
  success: boolean;
  business: {
    id: number;
    name: string;
    businessOwnerName?: string;
    businessOwnerEmail?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    type: string;
    merchantId?: string;
    userId: number;
    website?: string | null;
    subscriptionStatus?: LwSubscriptionStatus;
    source?: RecordSource;
    user: {
      id: number;
      firstname: string;
      lastname: string;
      email: string;
      phone?: string | null;
    };
    businessTheme?: {
      customUrl?: string;
      encodedLink?: string;
    };
    settings?: {
      dataSharingConsentDate?: string;
      isDataSharingConsented?: boolean;
    };
  };
}

export type Business = GetIndividualBusinessOutput['business'];

export const useIndividualBusiness = (businessId: string | number) => {
  return useQuery<unknown, ApiError, Business>({
    queryKey: ['individualBusiness', businessId],
    queryFn: async () => {
      if (!businessId) {
        return null;
      }

      const { data } = (await getIndividualBusiness(
        businessId,
      )) as AxiosResponse<GetIndividualBusinessOutput>;
      return data.business;
    },
    onError: (error) => {
      Sentry.captureException(error);

      if (!getIsNotFound(error)) {
        toast.error(error?.response?.data?.error ?? THEME_MESSAGES.somethingWrong, {
          duration: 4000,
        });
      }
      return error;
    },
    enabled: !!businessId,
  });
};
